import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Clickable } from "../../button/Clickable";
import { SiteLayout } from "../SiteLayout";
import { SitePageIntro } from "../SitePageIntro";
import styles from "./SiteLegalPage.scss";

/**
 * Legal document page (terms, privacy …) with a sticky table of contents
 * @param updated – date string shown as "Last updated …"
 * @param intro – optional content before the first section
 * @param sections – [{ id, title, content }]; `id` is the anchor used by the table of contents
 * @param otherDocument – { label, link } to the related legal document
 */
export const SiteLegalPage = ({ title, updated, intro, sections, otherDocument }) => {
    const intl = useIntl();

    // Open at the section in the URL hash (e.g. a shared /terms#general link)
    useEffect(() => {
        const id = window.location.hash.slice(1);
        if (id) {
            document.getElementById(decodeURIComponent(id))?.scrollIntoView();
        }
    }, []);

    // index.html sets <base href>, which makes plain "#id" links resolve to the site root.
    // Scroll within this page instead and keep the hash on the current URL.
    const handleSectionClick = (event, id) => {
        event.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(window.history.state, "", `${window.location.pathname}#${id}`);
    };

    return (
        <SiteLayout>
            <div className={styles.base}>
                <header className={styles.head}>
                    <SitePageIntro eyebrow={<FormattedMessage id="siteLegalPage.eyebrow" />} title={title}>
                        {updated ? (
                            <p className={styles.updated}>
                                <FormattedMessage id="siteLegalPage.lastUpdated" values={{ date: updated }} />
                            </p>
                        ) : null}
                    </SitePageIntro>
                </header>
                <div className={styles.layout}>
                    <aside className={styles.toc} aria-label={intl.formatMessage({ id: "siteLegalPage.onThisPage" })}>
                        <p className={styles.tocLabel}>
                            <FormattedMessage id="siteLegalPage.onThisPage" />
                        </p>
                        <ol>
                            {sections.map((section) => (
                                <li key={section.id}>
                                    <a
                                        href={`${window.location.pathname}#${section.id}`}
                                        onClick={(event) => handleSectionClick(event, section.id)}
                                    >
                                        {section.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                        {otherDocument ? (
                            <Clickable link={otherDocument.link} className={styles.other}>
                                {otherDocument.label} →
                            </Clickable>
                        ) : null}
                    </aside>
                    <article className={styles.body}>
                        {intro ? <div className={styles.intro}>{intro}</div> : null}
                        {sections.map((section) => (
                            <section key={section.id} id={section.id}>
                                <h2>{section.title}</h2>
                                {section.content}
                            </section>
                        ))}
                    </article>
                </div>
            </div>
        </SiteLayout>
    );
};
